import { Card, Colors, Heading, Icon, Text } from '@amelio/ui-library';
import { Fragment, useEffect, useState } from 'react';

import { CompetencyTranslationRow } from './CompetencyTranslationRow';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';
import type { LocalizedText } from '@/utils/localization';

import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { useAmelioTranslations } from '@/hooks';
import { getLanguageDisplayName, getTextByLanguage } from '@/utils/localization';

export type PerformanceScoreLevelTranslationField = 'label' | 'description';

export interface PerformanceScoreLevelTranslationChange {
    levelId: string;
    field: PerformanceScoreLevelTranslationField;
    value: string;
}

interface PerformanceScoreLevelsTranslationAccordionProps {
    levels: PerformanceScoreLevel[];
    sourceLanguage: string;
    targetLanguage: string;
    onChange: (change: PerformanceScoreLevelTranslationChange) => void;
}

const countScoreLevelProgress = (levels: PerformanceScoreLevel[], targetLanguage: string) => {
    let translated = 0;
    let total = 0;

    const count = (text: LocalizedText) => {
        total += 1;
        if ((text[targetLanguage] ?? '').trim().length > 0) {
            translated += 1;
        }
    };

    levels.forEach((level) => {
        count(level.label);
        count(level.description);
    });

    return { translated, total };
};

export const PerformanceScoreLevelsTranslationAccordion = ({
    levels,
    sourceLanguage,
    targetLanguage,
    onChange,
}: PerformanceScoreLevelsTranslationAccordionProps) => {
    const { t } = useAmelioTranslations();

    const progress = countScoreLevelProgress(levels, targetLanguage);
    const isComplete = progress.total > 0 && progress.translated === progress.total;

    const [isOpen, setIsOpen] = useState<boolean>(!isComplete);

    useEffect(() => {
        if (!isComplete) {
            setIsOpen(true);
        }
    }, [isComplete]);

    const source = (text: LocalizedText) => getTextByLanguage(text, sourceLanguage);
    const target = (text: LocalizedText) => text[targetLanguage] ?? '';

    return (
        <Card
            borderRadius={'2xl'}
            padding={'none'}
            className={'overflow-hidden'}
        >
            <button
                type={'button'}
                onClick={() => setIsOpen((open) => !open)}
                className={
                    'flex w-full flex-col items-center justify-between gap-2 border border-gray-100 bg-purple-50 px-6 py-4 text-left sm:flex-row'
                }
                aria-expanded={isOpen}
                data-testid={'translation-performance-score-levels-accordion'}
            >
                <Heading
                    level={5}
                    weight={'semibold'}
                    className={'text-md'}
                >
                    {t('competenciesTranslations.performanceScoreLevelsSection')}
                </Heading>

                <div className={'flex items-center gap-3'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'text-caption whitespace-nowrap'}
                        data-testid={'translation-performance-score-levels-count'}
                    >
                        {t('competenciesTranslations.fieldsTranslated', {
                            translated: progress.translated,
                            total: progress.total,
                        })}
                    </Text>

                    <Icon
                        name={isComplete ? 'essential-tick-circle' : 'essential-danger'}
                        color={isComplete ? Colors.SUCCESS : Colors.WARNING}
                    />

                    <Icon
                        name={'arrow-arrow-down'}
                        className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
                    />
                </div>
            </button>

            {isOpen && (
                <div className={'bg-white/80 px-6 py-4'}>
                    <div className={'border-base-100 flex gap-6 border-b pb-2'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption flex-1 uppercase'}
                        >
                            {getLanguageDisplayName(sourceLanguage)}
                        </Text>

                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption flex-1 uppercase'}
                        >
                            {sourceLanguage !== targetLanguage
                                ? getLanguageDisplayName(targetLanguage)
                                : ''}
                        </Text>
                    </div>

                    {levels.map((level) => {
                        if (!level.id) {
                            return null;
                        }

                        return (
                            <Fragment key={level.id}>
                                <div className={'flex items-center gap-3 pt-3'}>
                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_400}
                                        className={'text-caption'}
                                    >
                                        {source(level.label)}
                                    </Text>
                                </div>

                                <CompetencyTranslationRow
                                    variant={CompetencyTranslationFieldKind.COMPETENCY_NAME}
                                    sourceText={source(level.label)}
                                    targetValue={target(level.label)}
                                    onTargetChange={(value) =>
                                        onChange({ levelId: level.id!, field: 'label', value })
                                    }
                                />

                                <CompetencyTranslationRow
                                    variant={CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION}
                                    sourceText={source(level.description)}
                                    targetValue={target(level.description)}
                                    onTargetChange={(value) =>
                                        onChange({
                                            levelId: level.id!,
                                            field: 'description',
                                            value,
                                        })
                                    }
                                />
                            </Fragment>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};
